import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import { getUnlockMaterial, getSecurityState } from "../api/passwordManagerSecurityApi";
import { activatePasswordManager, getPasswordManagerStatus } from "../api/passwordManagerApi";
import { getActiveDbSummary } from "../api/passwordDatabaseApi";
import * as crypto from "../utils/crypto";

/**
 * CryptoContext
 * Sidebar is the single Crypto Host.
 * All crypto state (masterKey, privateKey, unlocked flag) lives here in-memory only.
 * No keys are ever stored in localStorage or sessionStorage.
 */

const CryptoContext = createContext(null);

export function useCryptoContext() {
  const ctx = useContext(CryptoContext);
  if (!ctx) {
    throw new Error("useCryptoContext must be used within a CryptoProvider");
  }
  return ctx;
}

export function CryptoProvider({ oid, children }) {
  // In-memory crypto state – cleared on page refresh
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [masterKey, setMasterKey] = useState(null); // Uint8Array (32 bytes)
  const [privateKey, setPrivateKey] = useState(null); // PEM string
  const [passwortDatenbankId, setPasswortDatenbankId] = useState(null);
  const [masterKeyVersion, setMasterKeyVersion] = useState(1);
  const [isOwner, setIsOwner] = useState(true);
  const [activeUserCount, setActiveUserCount] = useState(0);
  const [isPaid, setIsPaid] = useState(false);

  // Prevent concurrent unlock attempts
  const unlockingRef = useRef(false);

  /**
   * Performs the full unlock flow:
   * 1. Fetch unlock material from backend
   * 2. Decrypt private key with user password (Argon2id/PBKDF2 + AES-GCM)
   * 3. Handle no-active-DB case (reactivation)
   * 4. Decrypt MasterKey with private key (RSA-OAEP)
   * 5. Fetch ownership + user count
   *
   * Extracted from SecurityPage.jsx handleUnlock (lines 355-554)
   *
   * @param {string} password - User's Benutzer-Schlüssel
   * @returns {Promise<{ success: boolean, error?: string }>}
   */
  const unlock = useCallback(async (password) => {
    if (!oid) return { success: false, error: "Keine Benutzer-ID gefunden" };
    if (unlockingRef.current) return { success: false, error: "Entsperrung läuft bereits" };

    unlockingRef.current = true;

    try {
      // Step 1: Get unlock material from backend
      const response = await getUnlockMaterial(oid);
      if (!response.success) {
        return { success: false, error: response.message || "Fehler beim Entsperren" };
      }

      const {
        encryptedPrivateKey,
        privateKeyEnvelope,
        publicKey,
        encryptedMasterKey,
        masterKeyEnvelope,
        masterKeyVersion: unlockMkVersion,
        aktivPasswortDatenbankId,
      } = response.data;

      console.log("[CryptoContext] Unlock material received");
      console.log("[CryptoContext] KDF:", privateKeyEnvelope.kdf.algorithm);

      // Step 2: Decrypt private key with user password
      console.log("[CryptoContext] Decrypting private key...");
      const decryptedPrivateKey = await crypto.decryptPrivateKeyWithPassword(
        encryptedPrivateKey,
        privateKeyEnvelope,
        password,
      );
      console.log("[CryptoContext] Private key decrypted successfully");

      // Step 3: Check if active DB exists
      if (
        !aktivPasswortDatenbankId ||
        !encryptedMasterKey ||
        !masterKeyEnvelope
      ) {
        console.log("[CryptoContext] No active DB; attempting reactivation...");

        // Generate new MasterKey and activate PM (backend creates new own DB)
        const newMasterKey = crypto.generateMasterKey();
        const newEncryptedMasterKey = await crypto.encryptMasterKeyWithPublicKey(
          newMasterKey,
          publicKey,
        );
        const newEnvelope = crypto.createMasterKeyEnvelope(1, 1);

        const activateRequest = {
          oid,
          publicKey,
          encryptedPrivateKey,
          encryptedMasterKey: newEncryptedMasterKey,
          masterKeyEnvelope: JSON.stringify(newEnvelope),
        };

        const activateResponse = await activatePasswordManager(activateRequest);
        if (!activateResponse.success) {
          return {
            success: false,
            error: activateResponse.message || "Fehler beim Reaktivieren der Passwortdatenbank",
          };
        }

        console.log("[CryptoContext] Password manager reactivated with new DB");

        // Fetch summary for the newly created DB
        let reactivatedDbId = null;
        let reactivatedIsOwner = true;
        let reactivatedUserCount = 0;

        const summaryResp = await getActiveDbSummary(oid);
        if (summaryResp.success && summaryResp.data) {
          reactivatedDbId = summaryResp.data.aktivPasswortDatenbankId;
          reactivatedIsOwner = summaryResp.data.isOwnerOfActiveDb;
          reactivatedUserCount = summaryResp.data.activeUserCount;
        }

        // Check if paid
        let paid = false;
        try {
          const secState = await getSecurityState(oid);
          if (secState.success && secState.data) {
            paid = secState.data.isPaid || false;
          }
        } catch (_) { /* ignore */ }

        // Set all state
        setPrivateKey(decryptedPrivateKey);
        setMasterKey(newMasterKey);
        setPasswortDatenbankId(reactivatedDbId);
        setMasterKeyVersion(1);
        setIsOwner(reactivatedIsOwner);
        setActiveUserCount(reactivatedUserCount);
        setIsPaid(paid);
        setIsUnlocked(true);

        return { success: true };
      }

      // Step 4: Decrypt MasterKey with private key
      console.log("[CryptoContext] Decrypting MasterKey...");
      const decryptedMasterKey = await crypto.decryptMasterKeyWithPrivateKey(
        encryptedMasterKey,
        decryptedPrivateKey,
      );
      console.log("[CryptoContext] MasterKey decrypted successfully");

      // Step 5: If password manager is deactivated, reactivate it
      let paid = false;
      try {
        const secState = await getSecurityState(oid);
        if (secState.success && secState.data) {
          paid = secState.data.isPaid || false;
          if (!secState.data.passwortManagerAktiv) {
            console.log("[CryptoContext] Reactivating password manager...");
            const activateRequest = {
              oid,
              publicKey,
              encryptedPrivateKey,
              encryptedMasterKey,
              masterKeyEnvelope: masterKeyEnvelope ? JSON.stringify(masterKeyEnvelope) : null,
            };
            const activateResponse = await activatePasswordManager(activateRequest);
            if (!activateResponse.success) {
              return {
                success: false,
                error: activateResponse.message || "Fehler beim Reaktivieren der Passwortdatenbank",
              };
            }
            console.log("[CryptoContext] Password manager reactivated successfully");
          }
        }
      } catch (_) { /* ignore */ }

      // Step 6: Fetch ownership + user count
      let resolvedIsOwner = true;
      let resolvedUserCount = 0;

      if (aktivPasswortDatenbankId) {
        const summaryResp = await getActiveDbSummary(oid);
        if (summaryResp.success && summaryResp.data) {
          resolvedIsOwner = summaryResp.data.isOwnerOfActiveDb;
          resolvedUserCount = summaryResp.data.activeUserCount;
        }
      }

      // Set all state
      setPrivateKey(decryptedPrivateKey);
      setMasterKey(decryptedMasterKey);
      setPasswortDatenbankId(aktivPasswortDatenbankId);
      setMasterKeyVersion(unlockMkVersion || 1);
      setIsOwner(resolvedIsOwner);
      setActiveUserCount(resolvedUserCount);
      setIsPaid(paid);
      setIsUnlocked(true);

      return { success: true };
    } catch (err) {
      console.error("[CryptoContext] Error unlocking:", err);

      // Check for common errors
      if (
        err.message?.includes("Decryption failed") ||
        err.message?.includes("OperationError") ||
        err.name === "OperationError"
      ) {
        return { success: false, error: "Falsches Passwort. Bitte versuchen Sie es erneut." };
      }

      return {
        success: false,
        error: err.message || "Unbekannter Fehler beim Entsperren",
      };
    } finally {
      unlockingRef.current = false;
    }
  }, [oid]);

  /**
   * Clears all crypto state from memory.
   */
  const lock = useCallback(() => {
    setIsUnlocked(false);
    setMasterKey(null);
    setPrivateKey(null);
    setPasswortDatenbankId(null);
    setMasterKeyVersion(1);
    setIsOwner(true);
    setActiveUserCount(0);
    setIsPaid(false);
    console.log("[CryptoContext] Locked – all crypto state cleared");
  }, []);

  /**
   * Updates MasterKey and version after rotation (called from dialog via bridge).
   */
  const updateAfterRotation = useCallback((newMasterKey, newVersion) => {
    setMasterKey(newMasterKey);
    setMasterKeyVersion(newVersion);
    console.log("[CryptoContext] MasterKey rotated to version", newVersion);
  }, []);

  /**
   * Re-fetches status and re-decrypts MasterKey after a database change.
   * Handles fallback DB assignment and auto-deactivation.
   *
   * Extracted from PasswordManagerDialog.jsx refreshAfterDbChange (lines 61-154)
   *
   * @returns {Promise<{ deactivated: boolean }>}
   */
  const refreshAfterDbChange = useCallback(async () => {
    if (!oid) return { deactivated: false };

    try {
      // 1. Get updated status from backend
      const statusResponse = await getPasswordManagerStatus(oid);
      const statusData = statusResponse?.data;

      // PM auto-deactivation detection (Option-1 invariant)
      if (statusData && !statusData.passwortManagerAktiv) {
        lock();
        return { deactivated: true };
      }

      const newDbId = statusData?.aktivPasswortDatenbankId || null;

      if (!newDbId) {
        setPasswortDatenbankId(null);
        setMasterKey(null);
        setActiveUserCount(0);
        setIsOwner(true);
        return { deactivated: false };
      }

      // 2. Fallback DB exists – decrypt MasterKey for it
      if (!privateKey) {
        console.error("[CryptoContext] No private key available for fallback DB decrypt");
        setPasswortDatenbankId(null);
        setMasterKey(null);
        setActiveUserCount(0);
        setIsOwner(true);
        return { deactivated: false };
      }

      // Get unlock material (returns EncryptedMasterKey for the new active DB)
      const unlockResponse = await getUnlockMaterial(oid);
      if (!unlockResponse?.success || !unlockResponse?.data?.encryptedMasterKey) {
        console.error("[CryptoContext] Failed to get unlock material for fallback DB");
        setPasswortDatenbankId(newDbId);
        setMasterKey(null);
        setActiveUserCount(0);
        setIsOwner(true);
        return { deactivated: false };
      }

      // Decrypt MasterKey
      const decryptedMasterKey = await crypto.decryptMasterKeyWithPrivateKey(
        unlockResponse.data.encryptedMasterKey,
        privateKey,
      );

      // 3. Get ownership + user count
      let newUserCount = 0;
      let newIsOwner = true;
      const summaryResp = await getActiveDbSummary(oid);
      if (summaryResp?.success && summaryResp?.data) {
        newUserCount = summaryResp.data.activeUserCount || 0;
        newIsOwner = summaryResp.data.isOwnerOfActiveDb;
      }

      // 4. Update state
      const mkVersion = unlockResponse.data.masterKeyVersion || 1;
      setPasswortDatenbankId(newDbId);
      setMasterKey(decryptedMasterKey);
      setMasterKeyVersion(mkVersion);
      setActiveUserCount(newUserCount);
      setIsOwner(newIsOwner);

      return { deactivated: false };
    } catch (err) {
      console.error("[CryptoContext] Error refreshing after DB change:", err);
      setPasswortDatenbankId(null);
      setMasterKey(null);
      setActiveUserCount(0);
      setIsOwner(true);
      return { deactivated: false };
    }
  }, [oid, privateKey, lock]);

  const value = {
    // State
    isUnlocked,
    masterKey,
    privateKey,
    passwortDatenbankId,
    masterKeyVersion,
    isOwner,
    activeUserCount,
    isPaid,
    oid,

    // Actions
    unlock,
    lock,
    updateAfterRotation,
    refreshAfterDbChange,
  };

  return (
    <CryptoContext.Provider value={value}>
      {children}
    </CryptoContext.Provider>
  );
}
